import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  openForAppointment, openForTicket, openForThread, openForGroup, join, iceConfig,
  sendSignal, sendChat, reportSharing, endSession, leaveSession, leaveOnUnload, inviteToSession,
  chatPeople, pollLoop, shouldOffer, peersOf, namesOf
} from '../lib/sessionLink';
import {
  unlock, blocked, ping, startRinging, stopRinging, stopFlashing,
  flashTitle, buzz, silence, soundWanted, setSoundWanted
} from '../lib/chime';
import {
  remembered, remember, resolve, isGone, audioConstraints, videoConstraints, applySink
} from '../lib/devices';
import { DevicePanel } from '../components/CallButton';
import Recorder, { RecordingBanner, onRecordingSignal } from '../components/recording/Recorder';
import { sessionRecordings } from '../lib/recordings';
import { admitGuest } from '../lib/meetings';
import { useDraw } from '../components/draw/drawChannel';
import Whiteboard from '../components/draw/Whiteboard';
import Annotate from '../components/draw/Annotate';
import Avatar from '../components/Avatar';
import './SessionPage.css';

/**
 * The live session: the user shares their screen, everybody talks.
 *
 * Gordon's design, and the reason this page is as small as it is: the fixer
 * asks, the user presses a button, and the user does the clicking while
 * following spoken instructions. There is no remote control here and there is
 * no code in Jarvis that could add one, because the share comes from
 * getDisplayMedia() - which means THE BROWSER AND THE OPERATING SYSTEM own the
 * permission prompt, the persistent sharing indicator and the Stop button, and
 * nothing on this page can suppress any of the three. The user can stop
 * sharing from the operating system's own indicator without this page being
 * involved at all; all it can do is notice and report it.
 *
 * A FULL MESH, since Gordon asked for calls "not limited to 2 people". Every
 * pair of people in the call has its own RTCPeerConnection, held in a Map
 * keyed by the other person's participant id, and everything below that used
 * to be "the" connection is now "this person's" connection. Signals carry
 * from_id, so each one is routed to the entry it belongs to; nothing is
 * shared between entries except the microphone and whatever I am showing.
 * The cost is honest: a screen is sent once per other person, so this is for
 * a handful of people, not a room.
 *
 * ONE SIDE OF EACH PAIR OFFERS. WebRTC lets either peer offer, and if both do
 * at once the negotiation collides and the call fails in a way that looks
 * like bad luck. lib/sessionRoles' shouldOffer settles it per pair from data
 * both sides have, so neither has to be told - and with four people there
 * are six pairs, each settled on its own.
 *
 * AND THE SCREEN IS ATTACHED WITHOUT RENEGOTIATING. The offer declares a
 * video transceiver up front, sendonly and empty; starting a share puts the
 * screen track into it with replaceTrack - into EVERY peer's - and stopping
 * takes it out again. So there is exactly one offer/answer exchange per pair
 * in the life of a call, however many times the share is turned on and off.
 * The alternative - addTrack and a fresh negotiation each time - is where
 * glare comes back, and it comes back at the worst moment, in the middle of
 * somebody's call.
 *
 * HTTPS IS NOT OPTIONAL. getDisplayMedia and getUserMedia are both refused
 * outside a secure context, so this page cannot work over plain http. Said out
 * loud below rather than failing as a null, because "nothing happens when I
 * press Share" is otherwise an afternoon of somebody's time.
 */

const PRESENCE_TICK_MS = 1000;

/**
 * How long the ended page stays up when the OTHER side ended the call, so
 * the reason - "Rita could not take the call" - can be read before the page
 * goes back to where the call started. When this person ended it themselves
 * there is nothing to read, and it goes back at once.
 */
const RETURN_AFTER_MS = 2500;

/** What a peer's tile knows before anything has been heard from them. */
const FRESH = { connection: 'new', sharing: false, source: 'screen', audio: false };

/**
 * Where a session page may send somebody back to.
 *
 * A path on this site and nothing else: ?from= arrives in the URL, and a URL
 * is something anybody can type, so an absolute address or a protocol-
 * relative //host would turn the end of a call into a redirect to anywhere.
 */
const safePath = (p) => (/^\/(?!\/)/.test(String(p || '')) ? String(p) : null);

/** The label on the way out, from where it goes. */
const backLabel = (to) => {
  if (!to) return 'Go back';
  if (/^\/chat(\/|$)/.test(to)) return 'Back to the chat';
  if (/^\/(my-)?tickets\//.test(to)) return 'Back to the ticket';
  if (/^\/meetings\//.test(to)) return 'Back to the meeting';
  return 'Go back';
};

/** A duration a human can read, from seconds. */
const spoken = (seconds) => {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${String(s % 60).padStart(2, '0')}s`;
};

/** One peer connection's state, in words. Never only as a colour. */
const stateWords = (c) => ({
  connected: 'Connected',
  connecting: 'Connecting…',
  failed: 'Could not connect',
  disconnected: 'Reconnecting…'
}[c] || 'Setting up…');

/** Two letters for a tile with no video in it. */
const initialsOf = (name) => String(name || '?').split(/\s+/).filter(Boolean)
  .slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';

/**
 * What a session is about, for the header, the ringing card and the way out.
 *
 * On a ticket: the number and the title, and the ticket is where "back" goes.
 * On a chat group: the group's name, and the group is where "back" goes. On a
 * General thread (the firm's "calls without a ticket" switch): the other
 * person's name, and their conversation is where "back" goes. An
 * administrator looking in on two other people's call is on neither side, so
 * they get both names and "back" is wherever they came from.
 */
const aboutOf = (session, me, topicThread = null, guest = null) => {
  if (!session) return { title: guest?.meeting?.title || 'Session', sub: null, back: null };
  // A MEETING (migration 066): its title, and the meeting's page is where
  // "back" goes - for a guest nowhere, since they have no page to go to.
  const meetingId = session.meeting_id || session.meeting?.id || guest?.meeting?.id || null;
  if (meetingId || guest) {
    const title = session.meeting_title || session.meeting?.title || guest?.meeting?.title || 'Meeting';
    return {
      title,
      sub: 'Meeting',
      back: meetingId && !guest ? { to: `/meetings/${meetingId}`, label: 'Back to the meeting' } : null
    };
  }
  if (session.ticket_number) {
    // The TicketLink rule: a contractor's ticket page is /my-tickets. Decided
    // from the participant row the server handed back, which says exactly
    // who this person is, rather than only from a token that may still be
    // sitting in localStorage after a contractor signed in on this browser.
    const contractor = me ? me.is_contractor === true
      : Boolean(localStorage.getItem('contractor_token'));
    return {
      title: `${session.ticket_number} — ${session.ticket_title}`,
      sub: `About ${session.ticket_number}`,
      back: { to: `${contractor ? '/my-tickets' : '/tickets'}/${session.ticket_id}`,
              label: 'Back to the ticket' }
    };
  }
  if (session.chat_group_id) {
    return {
      title: session.group_name ? `#${session.group_name} call` : 'Group call',
      sub: 'Group call',
      back: { to: `/chat/group/${session.chat_group_id}`, label: 'Back to the group' }
    };
  }
  if (session.direct_thread_id) {
    const other = session.thread_other;
    const names = (session.thread_people || []).map((p) => p.name).filter(Boolean);
    return {
      title: other
        ? `Call with ${other.name}`
        : names.length ? `Call between ${names.join(' and ')}` : 'Call',
      sub: 'Not about a ticket',
      // Back to the topic the call was started from, when it was one.
      back: other
        ? { to: `/chat/${other.kind}/${other.id}${topicThread ? `?thread=${topicThread}` : ''}`,
            label: 'Back to the conversation' }
        : null
    };
  }
  return { title: 'Session', sub: null, back: null };
};

/**
 * Is this chat person already in the call?
 *
 * By key when the participant row carries one (the chat's own "u:<id>" /
 * "c:<id>"), which is exact; by name otherwise, which is the best a row
 * without a key allows and is only used to keep somebody out of a picker.
 */
const samePerson = (participant, row) => (participant?.key && row?.key
  ? participant.key === row.key
  : Boolean(participant?.display_name) && participant.display_name === row?.name);

/*
 * GUEST MODE (Gordon, 22 October: outside guests join a meeting "with a name
 * only"). `guest` is handed in by GuestJoinPage once POST /join/:token/session
 * has answered: { token, sessionId, participantId, name, lobby, video,
 * meeting: { id, title, organiser_name }, onDone(why, detail) }. The page
 * then runs as it does for everybody - the same mesh, chat, screen, devices
 * and recording banner - with these differences, each marked below: the
 * session is the one named rather than one to open; the credential is the
 * guest bearer (set by GuestJoinPage through lib/sessionLink); until the
 * waiting room lets them in, the feed says { lobby: 'waiting' } and the page
 * shows a waiting card instead of a call; there is no Recorder and no Add
 * people; and leaving hands control back to GuestJoinPage (onDone) rather
 * than navigating, because a guest has nowhere in Jarvis to go back to.
 */
const SessionPage = ({ guest = null }) => {
  // /tickets/:ticketId/session, /sessions/a/:appointmentId, /sessions/:sessionId
  // and /chat/:kind/:id/session all land here; the last is a call on the
  // General thread with one person, when the firm allows calls without a
  // ticket - or, with kind 'group', a call to a whole chat group.
  const { appointmentId, sessionId: directId, ticketId, kind, id: personId } = useParams();
  const navigate = useNavigate();
  // ?video=1 is CallButton's contract with this page: the choice made where
  // the call was started is the state the call opens in, rather than something
  // to go looking for once you are in it.
  const [search] = useSearchParams();
  const wantsVideo = search.get('video') === '1' || Boolean(guest?.video);
  /*
   * ?from= IS WHERE THE CALL STARTED, and where this page goes back to when
   * it is over. Gordon: "when i start a call from chat it should go back to
   * chat once the call is ended. Similarly if the call started from the
   * ticket it should go back to the ticket (for all users)." CallButton, the
   * ticket's own Start-one-now link and IncomingCall's Answer all set it from
   * wherever they were pressed, so the callee goes back to where they
   * accepted the call from, exactly as the caller does. With no origin - an
   * invitation link, a typed address - the fallback is the ticket page or the
   * thread, so it is never a dead end.
   */
  const cameFrom = safePath(search.get('from'));
  // ?thread= names a topic with the person (a chat conversation under
  // General, migration 061): the call lands in that thread, and "back" too.
  // A plain token only (the server checks it is one of ours); never anything
  // that could carry a path or a query onwards.
  const topicThread = /^[\w-]{1,80}$/.test(search.get('thread') || '') ? search.get('thread') : null;

  const [phase, setPhase] = useState('opening');
  const [error, setError] = useState(null);
  const [trouble, setTrouble] = useState(null);
  const [session, setSession] = useState(null);
  const [me, setMe] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [chat, setChat] = useState([]);
  const [draft, setDraft] = useState('');
  const [relay, setRelay] = useState(null);

  const [sharing, setSharing] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [micRefused, setMicRefused] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraRefused, setCameraRefused] = useState(false);
  // Whose video is blown up: fullscreen where it is allowed, a CSS mode where
  // it is not. See maximise() for why both. Null when nobody's is.
  const [bigId, setBigId] = useState(null);
  /*
   * WHAT EACH OTHER PERSON'S TILE SHOWS, keyed by participant id: their
   * connection state, whether they are sharing and what (from the 'sharing'
   * signal's `source`, so the page can label it rather than guess), and
   * whether any sound has arrived from them. State rather than the ref below
   * because the page renders from it; the connections themselves live in the
   * ref, because a render must never be what closes a call.
   */
  const [remote, setRemote] = useState({});
  const [startedAt, setStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  // Bumped when a connection is torn down for renegotiation, so the effect
  // that decides who to offer to looks again even though nobody came or went.
  const [meshTick, setMeshTick] = useState(0);

  // Gordon, after using it: a request to share and an arriving message should
  // both make a noise. `asked` is the fixer's side of that - they have asked
  // and are waiting; `askedToShare` is the user's - somebody is waiting on
  // them. Separate, because the two pages show completely different things.
  const [asked, setAsked] = useState(false);
  // Who was asked, by name, for the line that says the ask went out.
  const [askedWhom, setAskedWhom] = useState('');
  // How many times they have asked in this session, which is a different
  // question from whether an ask is outstanding: a refusal clears the waiting
  // state, and the button should still read "Ask again" afterwards rather than
  // pretending this is the first time.
  const [asks, setAsks] = useState(0);
  const [askedToShare, setAskedToShare] = useState(false);
  const [askedBy, setAskedBy] = useState(null);
  // Who said not just now, by name; null when nobody has.
  const [declinedBy, setDeclinedBy] = useState(null);
  const [sound, setSound] = useState(soundWanted);
  // True when the browser is refusing to make a noise until somebody clicks.
  // A silently refused ring is the exact failure this change exists to
  // prevent, so it is a state the page can show rather than a thing we hope
  // about.
  const [muzzled, setMuzzled] = useState(false);
  // Left rather than ended: the others are still talking.
  const [left, setLeft] = useState(false);

  /*
   * THE WAITING ROOM, both sides of it.
   *
   * A guest's side: `lobby` is 'waiting' until somebody inside lets them
   * in (the feed says so), 'refused' when somebody did not; the page shows
   * a card for the first and hands the second to GuestJoinPage. Staff's
   * side: `waitingGuests` is who is at the door, from 'lobby' signals -
   * { state: 'waiting' | 'admitted' | 'refused', participant_id, name } -
   * drawn as a "Waiting room (n)" chip with Admit / Refuse, and a chime
   * when somebody new arrives, because the organiser is looking at the
   * meeting, not at the door.
   */
  const [lobby, setLobby] = useState(guest?.lobby ? 'waiting' : null);
  // From the 'lobby' signals (named, so a chime can greet each arrival)...
  const [waitingGuests, setWaitingGuests] = useState([]);
  // ...and from the participants list, which carries lobby_state and is
  // the truth when the page arrived after the signal did. The panel shows
  // the union; a guest the list says is admitted or refused is dropped.
  const atTheDoor = useMemo(() => {
    const listed = participants.filter((p) => p.lobby_state === 'waiting')
      .map((p) => ({ id: p.id, name: p.display_name || 'A guest' }));
    const decided = new Set(participants.filter((p) => p.lobby_state && p.lobby_state !== 'waiting').map((p) => p.id));
    const seen = new Set(listed.map((g) => g.id));
    return [...listed, ...waitingGuests.filter((g) => !seen.has(g.id) && !decided.has(g.id))];
  }, [participants, waitingGuests]);
  const [lobbyOpen, setLobbyOpen] = useState(true);
  const [admitting, setAdmitting] = useState(new Set());

  // Adding people to a call that is already going.
  const [picking, setPicking] = useState(false);
  const [people, setPeople] = useState(null);
  const [pickTrouble, setPickTrouble] = useState(null);
  // "Ringing Rita…", one per person added and not yet arrived.
  const [ringingOut, setRingingOut] = useState([]);

  /*
   * WHICH MICROPHONE, SPEAKERS AND CAMERA (22 October). Read from what the
   * call menu remembered, and changeable here without hanging up. The ref
   * is what the stream-opening callbacks read, because they are created once
   * and must see the choice as it is now rather than as it was when they
   * were created; the state is what the panel renders from.
   */
  const [devices, setDevices] = useState(remembered);
  const devicesRef = useRef(devices);
  useEffect(() => { devicesRef.current = devices; }, [devices]);
  const [devicesOpen, setDevicesOpen] = useState(false);
  // "Your headset is not plugged in - using the system default." Shown in the
  // panel, and nowhere else: it is about a choice, not about the call.
  const [deviceNote, setDeviceNote] = useState(null);

  /*
   * THE MESH. participant id -> { pc, videoSender, theirVideo, pendingIce,
   * haveRemote, stream, audioStream }. A ref, never state: these are live
   * objects with callbacks on them, and copying them through renders is how
   * a stale one ends up answering an offer.
   */
  const meshRef = useRef(new Map());
  /** Candidates that arrived before the connection they belong to existed. */
  const earlyIce = useRef(new Map());
  /** Offers being made, so one cannot be started twice for the same person. */
  const offering = useRef(new Set());
  /** Each peer's <video> and <audio>, so a track arriving can find its element. */
  const videoEls = useRef(new Map());
  const audioEls = useRef(new Map());
  const iceRef = useRef(null);
  const micRef = useRef(null);
  const screenRef = useRef(null);
  const cameraRef = useRef(null);
  const selfVideoRef = useRef(null);
  const stageRef = useRef(null);
  const chatEndRef = useRef(null);
  const meRef = useRef(null);
  const participantsRef = useRef([]);
  const sessionIdRef = useRef(null);
  const startedRef = useRef(null);
  const askedFrom = useRef(null);
  const stopPollRef = useRef(null);
  const endedRef = useRef(false);

  /*
   * THE WHITEBOARD AND THE MARKS ON A SHARED SCREEN (Gordon, 22 October).
   * All of it lives in components/draw: one data channel per pair, created
   * with the connection (offer() below), and an engine that keeps the
   * strokes and repaints its own canvases. This page holds only what it
   * renders from - whether the board is open, and which share I am marking
   * up - so a pen moving thirty times a second never re-renders the call.
   */
  const nameById = useCallback((id) => participantsRef.current.find((p) => p.id === id)?.display_name
    || null, []);
  const { engine: drawEngine, board, openBoard, hideBoard } = useDraw(me, nameById);
  // The surface I am drawing on over a share ('share:<participant id>'), or null.
  const [annotating, setAnnotating] = useState(null);
  // "End the call for all 4 people?" - asked before, never after.
  const [confirmEnd, setConfirmEnd] = useState(false);
  // My own screen, small, so I can see what everybody sees - and mark it up.
  const previewRef = useRef(null);
  // Each tile's video, as a stable getter for its annotation layer.
  const videoGetters = useRef(new Map());
  const videoOf = (id) => {
    if (!videoGetters.current.has(id)) {
      videoGetters.current.set(id, () => (id === 'mine' ? previewRef.current : videoEls.current.get(id)));
    }
    return videoGetters.current.get(id);
  };

  /**
   * The live signal handler, held in a ref.
   *
   * WHY THIS IS NOT JUST `handle`. The polling loop is created once, when the
   * page mounts, and lives for the whole session - so it closes over the
   * FIRST render's handler. That handler's `answer` closed over `post`, which
   * closed over `session`, which on the first render was still null. The
   * result was a page that negotiated in one direction only: the offering
   * side worked, because its effect re-ran with a current closure, while the
   * answering side built its answer and its ICE candidates and then silently
   * dropped every one of them, because `post` had no session to send them to.
   * (`post` now reads the session id from a ref as well, so the same mistake
   * cannot be made twice from the other end.)
   *
   * Nothing about that fails loudly. There is no error, no rejected promise
   * and no log: the offer simply goes unanswered and the connection sits at
   * "Connecting…" until somebody blames the network. e2e/session.mjs caught
   * it by asserting that the fixer answered, which is the assertion this
   * comment exists to explain.
   */
  const handleRef = useRef(null);
  /**
   * Signals are handled ONE AT A TIME, in the order they arrived. With one
   * connection a candidate overtaking its offer was rare; with a connection
   * per person, an answer being built for Rita while Marco's candidates
   * arrive is the normal case, and each handler awaits. A chain keeps them in
   * order without any of them having to know about the others.
   */
  const queueRef = useRef(Promise.resolve());
  /*
   * WHEN EACH PERSON LAST SAID THEY STOPPED SHARING. The video element's own
   * "a picture has arrived" (onLoadedMetadata / onResize below) is the
   * fallback for a 'sharing' report that never came - but a picture can
   * also arrive just AFTER the report that sharing stopped: a frame already
   * on the wire, or a resize fired late. Read as a fresh share, that put a
   * stopped screen's tile straight back up on the watcher's page, frozen on
   * its last frame - the opposite of "stop sharing". So for a few seconds
   * after an explicit stop the fallback is not believed; a real new share
   * arrives with its own report, which clears this.
   */
  const stoppedAt = useRef(new Map());
  /** Chat already seen, so a re-delivered batch cannot ping twice. */
  const pinged = useRef(new Set());

  // Somebody still at the door, or turned away, is not in the call: no
  // connection is offered to them and they are not counted as here.
  const inside = useMemo(() => participants.filter((p) => !p.lobby_state || p.lobby_state === 'admitted'), [participants]);
  const peers = useMemo(() => peersOf(me, inside), [me, inside]);
  // For the way out when the page goes away, which runs outside any render.
  const peersRef = useRef([]);
  useEffect(() => { peersRef.current = peers; }, [peers]);
  // A question about ending it for three people is moot once there are two.
  useEffect(() => { if (peers.length < 2) setConfirmEnd(false); }, [peers.length]);

  /**
   * Let the page make a noise.
   *
   * Browsers refuse audio until the page itself has had a real user gesture,
   * and ARRIVING BY FOLLOWING A LINK DOES NOT COUNT - the click happened on
   * the page before. So the first ring can be refused in silence, which is the
   * worst possible outcome for a feature whose whole job is to be heard. This
   * takes the first interaction of any kind, which in practice is the user
   * clicking almost anything, and opens the gate with it.
   */
  useEffect(() => {
    const open = () => { unlock(); setMuzzled(blocked()); };
    for (const type of ['pointerdown', 'keydown', 'touchstart']) {
      window.addEventListener(type, open, { once: true, passive: true });
    }
    return () => {
      for (const type of ['pointerdown', 'keydown', 'touchstart']) {
        window.removeEventListener(type, open);
      }
    };
  }, []);

  /** Looking at the page is the answer to a flashing title. */
  useEffect(() => {
    const seen = () => { if (!document.hidden) stopFlashing(); };
    document.addEventListener('visibilitychange', seen);
    window.addEventListener('focus', seen);
    return () => {
      document.removeEventListener('visibilitychange', seen);
      window.removeEventListener('focus', seen);
    };
  }, []);

  /** Nothing keeps ringing after the page has gone. */
  useEffect(() => () => silence(), []);

  useEffect(() => { meRef.current = me; }, [me]);
  useEffect(() => { participantsRef.current = participants; }, [participants]);

  const secure = typeof window !== 'undefined'
    && (window.isSecureContext || window.location.hostname === 'localhost');

  /* ------------------------------------------------------------ the call */

  const post = useCallback((kind, payload, to) => {
    const id = sessionIdRef.current;
    if (!id) return Promise.resolve();
    return sendSignal(id, { kind, payload, to: to || null })
      // A signal that cannot be sent is not worth an error banner on its own -
      // there will be dozens of candidates and losing one is normal - but it
      // must not be swallowed silently either.
      .catch((err) => { console.warn(`Could not send ${kind}:`, err.message); });
  }, []);

  /** Change one peer's tile, without a render when nothing changed. */
  const patchRemote = useCallback((id, patch) => {
    setRemote((prev) => {
      const cur = prev[id] || FRESH;
      if (Object.keys(patch).every((k) => cur[k] === patch[k])) return prev;
      return { ...prev, [id]: { ...cur, ...patch } };
    });
  }, []);

  /** What I am currently showing, if anything: one video channel each. */
  const localVideo = useCallback(() => {
    const screen = screenRef.current?.getVideoTracks()[0];
    if (screen && screen.readyState !== 'ended') return { track: screen, source: 'screen' };
    const cam = cameraRef.current?.getVideoTracks()[0];
    if (cam && cam.readyState !== 'ended') return { track: cam, source: 'camera' };
    return null;
  }, []);

  /** Close ONE person's connection, leaving everybody else's alone. */
  const closeEntry = useCallback((id) => {
    const entry = meshRef.current.get(id);
    if (!entry) return null;
    meshRef.current.delete(id);
    try { entry.pc.close(); } catch { /* already gone */ }
    // Their drawing channel goes with the connection, and so do the marks on
    // their screen: whatever they were sharing is not being shared any more.
    drawEngine.drop(id);
    drawEngine.forget(`share:${id}`);
    patchRemote(id, { connection: 'new', sharing: false, audio: false });
    return entry;
  }, [patchRemote, drawEngine]);

  /** Every connection, and the camera with them: the call is over for me. */
  const closeAll = useCallback(() => {
    for (const id of [...meshRef.current.keys()]) closeEntry(id);
    earlyIce.current.clear();
    try { cameraRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
    cameraRef.current = null;
    drawEngine.reset();
  }, [closeEntry, drawEngine]);

  /**
   * Open a stream on the chosen device of one kind, falling back to the
   * system default when that device is not there.
   *
   * The remembered headset is in a drawer: asking for it by `exact` id fails
   * with NotFoundError, and a call that cannot start because of a drawer is
   * not acceptable. So the default is asked for instead, and the panel says
   * which device was expected - the one line that tells somebody to plug it
   * in. A refusal (NotAllowedError) is NOT retried: that is a decision, and
   * asking twice is nagging.
   */
  const openChosen = useCallback(async (kind) => {
    const want = resolve(kind, null, devicesRef.current);
    const constraints = (id) => (kind === 'videoinput'
      ? { video: videoConstraints(id), audio: false }
      : { audio: audioConstraints(id), video: false });
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints(want.deviceId));
      setDeviceNote(null);
      return stream;
    } catch (err) {
      if (!want.deviceId || !isGone(err)) throw err;
      const was = devicesRef.current[kind]?.label;
      setDeviceNote(`${was ? `"${was}"` : `The remembered ${kind === 'videoinput' ? 'camera' : 'microphone'}`}`
        + ' is not plugged in — using the system default.');
      return navigator.mediaDevices.getUserMedia(constraints(null));
    }
  }, []);

  /** The microphone, asked for once and shared by every connection. */
  const mic = useCallback(async () => {
    if (micRef.current) return micRef.current;
    if (!secure || !navigator.mediaDevices?.getUserMedia) return null;
    try {
      const stream = await openChosen('audioinput');
      micRef.current = stream;
      setMicOn(true);
      setMicRefused(false);
      return stream;
    } catch {
      // Refused, or no microphone at all. The session is still worth having -
      // a shared screen and a chat box is most of the value - so this is a
      // state to report rather than a failure to stop on.
      setMicRefused(true);
      setMicOn(false);
      return null;
    }
  }, [secure, openChosen]);

  /**
   * A new connection to one person, registered under their id.
   *
   * Every callback checks that its entry is still the one registered for
   * that person. A connection that was replaced - they reloaded, and a fresh
   * one was negotiated - can still fire a late event, and a late event from
   * the old one must not paint over the new one's tile.
   */
  const createEntry = useCallback(async (id, carried = []) => {
    const audio = await mic();
    const config = iceRef.current || { iceServers: [] };
    const pc = new RTCPeerConnection({ iceServers: config.iceServers || [] });
    const entry = {
      id, pc, videoSender: null, theirVideo: null, haveRemote: false,
      pendingIce: [...carried, ...(earlyIce.current.get(id) || [])],
      stream: null, audioStream: null,
      // The sender my microphone went into, kept so that changing
      // microphone mid-call is a replaceTrack here rather than a renegotiation.
      audioSender: null
    };
    earlyIce.current.delete(id);
    const live = () => meshRef.current.get(id) === entry;
    // The drawing channel, when it is the other side that created it.
    drawEngine.attachAnswer(pc, id);

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        post('candidate', event.candidate.toJSON ? event.candidate.toJSON() : event.candidate, id);
      }
    };
    pc.onconnectionstatechange = () => {
      if (!live()) return;
      patchRemote(id, { connection: pc.connectionState });
      if (pc.connectionState === 'connected' && !startedRef.current) {
        startedRef.current = Date.now();
        setStartedAt(startedRef.current);
      }
    };
    pc.ontrack = (event) => {
      if (!live()) return;
      const stream = event.streams[0] || new MediaStream([event.track]);
      if (event.track.kind === 'video') {
        entry.stream = stream;
        const el = videoEls.current.get(id);
        if (el && el.srcObject !== stream) el.srcObject = stream;

        // A TRACK ARRIVING IS NOT A SCREEN ARRIVING, and assuming it was is a
        // real bug this page had. The offer declares an EMPTY sendonly video
        // transceiver up front - that is what lets a share start later without
        // renegotiating - and the receiving end fires ontrack for it the
        // moment the connection is established, with a track that is muted
        // because there is nothing in it yet. Reading that as "their screen is
        // shared" meant the fixer's page announced a share that had not
        // happened, showed an empty video frame for it, and hid the button for
        // asking them to share, all in the same instant the call connected.
        // The subsequent onmute put it right a beat later, which made it a
        // race rather than a steady wrong answer - the worse of the two.
        //
        // AND `muted` AT THIS INSTANT IS NOT THE ANSWER EITHER. Chromium
        // reports the brand-new receiver track as unmuted before any frame has
        // arrived, so reading it here is the same wrong answer by a slightly
        // longer route. There is no property of a freshly-arrived track that
        // distinguishes "an empty transceiver" from "a screen".
        //
        // So nothing is concluded here. Sharing is reported by the side doing
        // it - a 'sharing' signal, which is authoritative because that page is
        // the only thing that knows - and the track events below are the
        // corrective for the case where that report never arrived: a sender
        // whose tab was closed sends no message, but their track ends.
        // Nor is `onunmute`: Chromium fires it for the empty transceiver too,
        // as soon as the transport is up. Measured, not assumed - it is what
        // made the second attempt at this fail in exactly the same way as the
        // first.
        //
        // So the track events are NEGATIVES ONLY. They can turn sharing off,
        // which is always safe: a false "stopped" is corrected by the next
        // report, whereas a false "started" shows an empty frame and hides the
        // button for asking them to share.
        event.track.onended = () => { if (live()) patchRemote(id, { sharing: false }); };
        event.track.onmute = () => { if (live()) patchRemote(id, { sharing: false }); };
      } else {
        entry.audioStream = stream;
        const el = audioEls.current.get(id);
        if (el && el.srcObject !== stream) el.srcObject = stream;
        patchRemote(id, { audio: true });
      }
    };

    if (audio) {
      audio.getAudioTracks().forEach((t) => {
        const sender = pc.addTrack(t, audio);
        if (!entry.audioSender) entry.audioSender = sender;
      });
    }
    meshRef.current.set(id, entry);
    patchRemote(id, { connection: pc.connectionState || 'new' });
    return entry;
  }, [mic, post, patchRemote, drawEngine]);

  /**
   * A connection is up and I am already showing something: put it in.
   *
   * The late joiner's case. Everybody already in the call has my screen; the
   * person who just arrived has an empty slot and no 'sharing' report, because
   * that went out before they were here. So the track goes into their sender
   * and they are told, addressed to them alone - the others know already.
   */
  const tellLate = useCallback((entry, local) => {
    if (local) post('sharing', { sharing: true, source: local.source }, entry.id);
  }, [post]);

  /**
   * Make the offer, to one person.
   *
   * The video transceivers are declared HERE, so that starting a share later
   * is a replaceTrack rather than a renegotiation.
   */
  const offer = useCallback(async (peer) => {
    const id = peer.id;
    if (meshRef.current.has(id) || offering.current.has(id)) return;
    offering.current.add(id);
    try {
      const entry = await createEntry(id);
      const { pc } = entry;
      /*
       * TWO VIDEO SLOTS, DECLARED UP FRONT, and this is what makes a video
       * call possible without ever renegotiating.
       *
       * The first is mine to send on. The second is declared recvonly from
       * here, which arrives at the far end MIRRORED as sendonly - so the
       * answering side has a slot of its own to put a camera into, using
       * replaceTrack, without needing to add a transceiver and start a
       * second negotiation. One offer, one answer, for the life of the
       * pair, however many times either side turns a camera or a screen on
       * and off.
       *
       * Declaring them both in the offer is the whole trick: a transceiver
       * added later is a renegotiation, and a renegotiation is where glare
       * comes back - in the middle of somebody's call.
       */
      const mine = pc.addTransceiver('video', { direction: 'sendonly' });
      entry.videoSender = mine.sender;
      entry.theirVideo = pc.addTransceiver('video', { direction: 'recvonly' });
      /*
       * AND THE DRAWING CHANNEL, for the same reason and at the same moment:
       * created before createOffer it is part of this one offer (an
       * m=application line), so the whiteboard needs no negotiation of its
       * own either. The answering side picks it up in ondatachannel.
       */
      drawEngine.attachOffer(pc, id);
      const local = localVideo();
      if (local) await entry.videoSender.replaceTrack(local.track);

      const sdp = await pc.createOffer();
      await pc.setLocalDescription(sdp);
      await post('offer', { type: sdp.type, sdp: sdp.sdp }, id);
      tellLate(entry, local);
    } catch (err) {
      closeEntry(id);
      console.warn('Could not offer:', err.message);
      setTrouble('Could not start the call. Try reloading the page.');
    } finally {
      offering.current.delete(id);
    }
  }, [createEntry, post, tellLate, closeEntry, localVideo, drawEngine]);

  const answer = useCallback(async (from, payload) => {
    // An offer from somebody I already have a connection with is a fresh
    // start on their side - they reloaded, or their page lost the call - so
    // the old one is closed and this one answered from nothing. Any
    // candidates it was still holding are carried over; one that belongs to
    // the old negotiation fails harmlessly when applied.
    const old = closeEntry(from);
    try {
      const entry = await createEntry(from, old ? old.pendingIce : []);
      const { pc } = entry;
      await pc.setRemoteDescription(payload);
      entry.haveRemote = true;
      // Candidates routinely arrive before the description they belong to, and
      // addIceCandidate throws if they are applied first. Queued, then flushed.
      for (const c of entry.pendingIce.splice(0)) {
        await pc.addIceCandidate(c).catch(() => {});
      }
      /*
       * CLAIM THE SLOT THE OFFER LEFT FOR ME.
       *
       * The offer declares two video transceivers: the first is theirs to
       * send on, the second is mine. Mirrored onto this side, the first is
       * recvonly and the second can send - so the second is the one a camera
       * goes into. Found by direction where the browser has said which can
       * send, and by m-line order where it has not, which is the order they
       * were declared in.
       */
      const videos = pc.getTransceivers().filter((t) => t.receiver?.track?.kind === 'video');
      for (const t of videos) {
        if (/sendrecv|sendonly/.test(t.direction || '')) {
          if (!entry.videoSender) { entry.videoSender = t.sender; entry.mineTx = t; }
        } else if (!entry.theirVideo) {
          entry.theirVideo = t;
        }
      }
      if (!entry.theirVideo) entry.theirVideo = videos[0] || null;
      if (!entry.videoSender && videos[1]) {
        entry.videoSender = videos[1].sender;
        entry.mineTx = videos[1];
      }
      /*
       * AND SAY THAT I WILL SEND ON IT. A transceiver created by a remote
       * offer starts out recvonly, and an answer built from that declines to
       * send - so the slot was there and nothing could ever go into it. With
       * two people that never showed, because the user always offers and the
       * user is the one sharing. In a mesh the answerer is often the one
       * sharing: two users settle it by id, and the one who answered still
       * has to be able to show their screen.
       */
      if (entry.mineTx && !/send/.test(entry.mineTx.direction || '')) {
        try { entry.mineTx.direction = 'sendonly'; } catch { /* read-only here */ }
      }
      const local = localVideo();
      if (local && entry.videoSender) await entry.videoSender.replaceTrack(local.track);

      const sdp = await pc.createAnswer();
      await pc.setLocalDescription(sdp);
      await post('answer', { type: sdp.type, sdp: sdp.sdp }, from);
      tellLate(entry, local);
    } catch (err) {
      console.warn('Could not answer:', err.message);
      setTrouble('Could not join the call. Try reloading the page.');
    }
  }, [createEntry, closeEntry, post, tellLate, localVideo]);

  /* ------------------------------------------------------------- sharing */

  /** Put a track - or nothing - into every connection's video slot. */
  const intoEverySender = useCallback((track) => {
    for (const entry of meshRef.current.values()) {
      // A connection still being negotiated has no sender yet. It is not
      // forgotten: offer() and answer() put whatever I am showing into the
      // slot the moment they have one, and tell that person so.
      if (entry.videoSender) entry.videoSender.replaceTrack(track).catch(() => {});
    }
  }, []);

  /*
   * STOP, from wherever it is pressed: the footer, the pill at the top of
   * the stage, or the browser's own "Stop sharing" bar (track.onended, set
   * below). All three land here, so all three do the same things: end the
   * track, take it out of EVERY connection's sender, tell the server (which
   * tells everybody, so their tiles clear), and wipe the marks made on it.
   */
  const stopSharing = useCallback(() => {
    const had = Boolean(screenRef.current);
    try { screenRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
    screenRef.current = null;
    if (had) intoEverySender(null);
    setSharing(false);
    if (had && sessionIdRef.current) reportSharing(sessionIdRef.current, false).catch(() => {});
    const mine = meRef.current?.id;
    if (had && mine) {
      // Everybody forgets them when the share's end reaches them; saying it
      // over the data channel as well is quicker than the poll.
      drawEngine.clear(`share:${mine}`, 'all');
      drawEngine.forget(`share:${mine}`);
    }
    setAnnotating((a) => (a === `share:${mine}` ? null : a));
  }, [intoEverySender, drawEngine]);

  const startSharing = useCallback(async () => {
    if (!secure) {
      setTrouble('Screen sharing needs a secure connection (https).');
      return;
    }
    if (!navigator.mediaDevices?.getDisplayMedia) {
      setTrouble('This browser cannot share a screen. Chrome, Edge, Firefox and '
        + 'Safari 13 or later can.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 10 }, audio: false
      });
      screenRef.current = stream;
      const track = stream.getVideoTracks()[0];
      // To everybody in the call at once: one share, every connection.
      intoEverySender(track);

      setSharing(true);
      setAskedToShare(false);
      stopRinging();
      stopFlashing();
      // Not swallowed: if this fails the others have to find out from the
      // frames arriving, which works but is slower, and the person sharing
      // should know their screen is up before Jarvis has said so. The server
      // passes it on to everybody as a 'sharing' signal.
      reportSharing(sessionIdRef.current, true).catch(() => {
        setTrouble('Your screen is sharing, but Jarvis could not tell them yet.');
      });

      // THE STOP BUTTON LIVES IN THE OPERATING SYSTEM, not on this page. When
      // the user presses it the track ends and nothing else happens, so this
      // is the only place Jarvis finds out.
      track.onended = () => { stopSharing(); };
    } catch (err) {
      // A refusal is a decision, not an error: the prompt is the browser's and
      // saying no to it is the whole point of it being there.
      if (err?.name === 'NotAllowedError') return;
      console.warn('Could not share:', err.message);
      setTrouble('Could not start sharing your screen.');
    }
  }, [secure, stopSharing, intoEverySender]);

  /* ---------------------------------------------------------- asking */

  /**
   * "Could I see your screen?"
   *
   * The fixer's one new control, and the reason it exists: before this, the
   * only way to ask was to type it into a chat box the user was not looking
   * at, because the user is looking at the broken machine. An ask is an act
   * with a time on it, so the other page can ring.
   *
   * In a group it goes to the USERS in the call, addressed to each - the
   * people whose machines this is about - rather than ringing every other
   * member of IT as well. With nobody but IT here, it goes to all of them.
   */
  const askTargets = useMemo(() => {
    const users = peers.filter((p) => p.role === 'user');
    return users.length ? users : peers;
  }, [peers]);

  const askToShare = useCallback(async () => {
    setDeclinedBy(null);
    setAsked(true);
    setAsks((n) => n + 1);
    setAskedWhom(namesOf(askTargets, () => ''));
    await Promise.all(askTargets.map((p) => post('ask-share', {}, p.id)));
  }, [post, askTargets]);

  /** Answering it, either way. Both stop the noise. */
  const acceptAsk = useCallback(() => {
    setAskedToShare(false);
    stopRinging();
    stopFlashing();
    startSharing();
  }, [startSharing]);

  const declineAsk = useCallback(async () => {
    setAskedToShare(false);
    stopRinging();
    stopFlashing();
    await post('decline-share', {}, askedFrom.current);
  }, [post]);

  /** Sound on or off, remembered for this browser. */
  const toggleSound = useCallback(() => {
    const on = setSoundWanted(!sound);
    setSound(on);
    if (on) { unlock(); setMuzzled(blocked()); }
  }, [sound]);

  /** The browser was refusing; this click is the gesture that fixes it. */
  const enableSound = useCallback(() => {
    unlock();
    setMuzzled(blocked());
    if (askedToShare) startRinging();
  }, [askedToShare]);

  /* ------------------------------------------------------------- camera */

  const stopCamera = useCallback(async () => {
    try { cameraRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
    cameraRef.current = null;
    intoEverySender(null);
    setCameraOn(false);
    await post('sharing', { sharing: false, source: 'camera' }, null);
  }, [post, intoEverySender]);

  /**
   * Turn the camera on, into the one video slot I have on each connection.
   *
   * ONE SLOT PER PERSON, so a camera and a screen share are exclusive, and the
   * page says so rather than silently dropping one. That is a real limitation
   * and it is the right trade: carrying both would mean four video
   * transceivers and a negotiation that has to grow, for a case that does not
   * arise - in a support session the screen IS the point, and a camera is for
   * "let me see who I am talking to", which is the part before the screen goes
   * up.
   *
   * NOT REPORTED AS SHARING. routes/sessions.js counts screen-share seconds
   * and writes "shared their screen for 11 min" on the ticket; a camera is not
   * that, and recording it as one would put a false line in an audit record.
   * So the others are told by signal - to everybody at once - and the server
   * is not told at all.
   */
  const startCamera = useCallback(async () => {
    if (!secure || !navigator.mediaDevices?.getUserMedia) {
      setTrouble('A camera needs a secure connection (https).');
      return;
    }
    if (sharing) {
      setTrouble('Stop sharing your screen first — there is one video channel each.');
      return;
    }
    try {
      // The chosen camera, or the front one; a chosen camera that has gone
      // falls back to the default with a note in the devices panel.
      const stream = await openChosen('videoinput');
      cameraRef.current = stream;
      const track = stream.getVideoTracks()[0];
      intoEverySender(track);
      setCameraOn(true);
      await post('sharing', { sharing: true, source: 'camera' }, null);
      track.onended = () => { stopCamera(); };
    } catch (err) {
      // A refusal is a decision, not a fault: the prompt is the browser's.
      if (err?.name === 'NotAllowedError') { setCameraRefused(true); return; }
      setTrouble('Could not turn your camera on.');
    }
  }, [secure, sharing, post, stopCamera, intoEverySender, openChosen]);

  /* ------------------------------------------------------------ devices */

  /**
   * Change a device without hanging up.
   *
   * The choice is remembered first, whatever happens next, so that the next
   * call opens with it even if this one could not switch. Then:
   *
   *   MICROPHONE: a new stream on the new device, its track put into every
   *   connection's audio sender with replaceTrack - no renegotiation, the
   *   same trick the screen share uses - and the old stream stopped, which
   *   is what turns the old device's recording light off. Muted stays muted:
   *   the new track inherits `enabled`.
   *
   *   CAMERA: the same, into the video sender, and only when the camera is
   *   on. Off, there is nothing to swap; the next Camera on reads the choice.
   *
   *   SPEAKERS: setSinkId on every element that plays somebody else - the
   *   effect below does it, keyed on the choice, and the element refs do it
   *   for elements drawn later. Nothing on the sending side changes.
   *
   * A device that has gone between being listed and being chosen falls back
   * to the default with the same note as at the start of the call.
   */
  const chooseDevice = useCallback(async (kind, device) => {
    const next = remember(kind, device);
    devicesRef.current = next;
    setDevices(next);
    setDeviceNote(null);
    if (!secure || !navigator.mediaDevices?.getUserMedia) return;
    try {
      if (kind === 'audioinput' && micRef.current) {
        const stream = await openChosen('audioinput');
        const track = stream.getAudioTracks()[0];
        if (track) track.enabled = micOn;
        for (const entry of meshRef.current.values()) {
          if (entry.audioSender) await entry.audioSender.replaceTrack(track).catch(() => {});
        }
        const old = micRef.current;
        micRef.current = stream;
        try { old?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
        setMicRefused(false);
      } else if (kind === 'videoinput' && cameraRef.current) {
        const stream = await openChosen('videoinput');
        const track = stream.getVideoTracks()[0];
        const old = cameraRef.current;
        cameraRef.current = stream;
        intoEverySender(track);
        try { old?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
        track.onended = () => { stopCamera(); };
      }
      // A fresh object, so the panel's "Now:" lines - read off the live
      // tracks - are drawn again with the new device's name.
      setDevices({ ...remembered() });
    } catch (err) {
      if (err?.name === 'NotAllowedError') return;
      setDeviceNote(`Could not switch to that ${kind === 'videoinput' ? 'camera' : 'microphone'}`
        + ' — still using the one before.');
    }
  }, [secure, micOn, openChosen, intoEverySender, stopCamera]);

  /** The speakers, for everybody already on the page; new tiles get it as they are drawn. */
  useEffect(() => {
    const sink = devices.audiooutput?.id || '';
    for (const el of audioEls.current.values()) applySink(el, sink);
    for (const el of videoEls.current.values()) applySink(el, sink);
  }, [devices.audiooutput]);

  /** What is actually open, by the track's own name, for the panel's "Now:" lines. */
  const activeDevices = {
    audioinput: micRef.current?.getAudioTracks()[0]?.label || null,
    videoinput: cameraOn ? (cameraRef.current?.getVideoTracks()[0]?.label || null) : null
  };

  const toggleCamera = useCallback(
    () => (cameraOn ? stopCamera() : startCamera()),
    [cameraOn, startCamera, stopCamera]);

  /*
   * MY OWN CAMERA, in my own small tile. Not my screen: a preview of a shared
   * window that contains this page is a hall of mirrors, and the person
   * sharing is looking at the real thing anyway.
   */
  useEffect(() => {
    const el = selfVideoRef.current;
    if (!el) return;
    const want = cameraOn ? cameraRef.current : null;
    if (el.srcObject !== want) el.srcObject = want;
  });

  /*
   * AND MY SCREEN, while I share it, in a preview tile of its own. Gordon's
   * annotation needs the presenter to see the marks everybody else is making,
   * and the marks are drawn over a picture of the share - so the presenter
   * gets the picture too, the way Teams shows "you are presenting". It is
   * what the others see, which is the useful thing to be able to check.
   */
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const want = sharing ? screenRef.current : null;
    try { if (el.srcObject !== want) el.srcObject = want; } catch { /* not a real stream */ }
  });

  /* ----------------------------------------------------------- maximise */

  /**
   * Make somebody's incoming screen as big as the device allows.
   *
   * Gordon: "when there is a screen share allow the screen share on the
   * contractor/admin view to be allowed to be maximised." Which is the whole
   * point of watching somebody's screen - at a third of a laptop window you
   * cannot read their error message, which is the thing you are there to read.
   * In a group it is one person's tile that fills the view, and the others
   * step out of the way until it is put back.
   *
   * TWO MECHANISMS, because the first one is not always allowed. The Fullscreen
   * API is the right answer where it works. It is refused outright on iOS
   * Safari for anything but a <video> element played natively, and inside any
   * iframe without an allow-fullscreen attribute - and a button that does
   * nothing is worse than no button. So a refusal falls back to a CSS mode
   * that fills the viewport, which needs no permission and works everywhere.
   */
  const maximise = useCallback(async (id) => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      try { await document.exitFullscreen(); } catch { /* already out */ }
      setBigId(null);
      return;
    }
    if (bigId) { setBigId(null); return; }
    try {
      if (el.requestFullscreen) {
        await el.requestFullscreen({ navigationUI: 'hide' });
        setBigId(id);
        return;
      }
      // Safari on iPhone will do this for the video itself and nothing else.
      const video = videoEls.current.get(id);
      if (video?.webkitEnterFullscreen) { video.webkitEnterFullscreen(); return; }
      setBigId(id);
    } catch {
      // Refused - an iframe with no allow-fullscreen, or a policy. The CSS
      // mode needs no permission.
      setBigId(id);
    }
  }, [bigId]);

  // Leaving fullscreen by pressing Escape does not call anything of ours, so
  // the button would be left saying "Exit full screen" over a small video.
  useEffect(() => {
    const sync = () => { if (!document.fullscreenElement) setBigId(null); };
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  // The person whose screen filled the view stopped sharing, or left: there
  // is nothing left to be big.
  useEffect(() => {
    if (!bigId) return;
    if (!peers.some((p) => p.id === bigId) || !remote[bigId]?.sharing) {
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
      setBigId(null);
    }
  }, [bigId, peers, remote]);

  const toggleMic = useCallback(async () => {
    const stream = micRef.current || await mic();
    if (!stream) return;
    const on = !micOn;
    // One microphone, in every connection, so one switch mutes it for all.
    stream.getAudioTracks().forEach((t) => { t.enabled = on; });
    setMicOn(on);
  }, [micOn, mic]);

  /* --------------------------------------------------------- the lifecycle */

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setPhase('opening');
        const opened = guest
          ? { id: guest.sessionId }
          : appointmentId
          ? await openForAppointment(appointmentId)
          : ticketId
            ? await openForTicket(ticketId)
            : kind === 'group' && personId
              ? await openForGroup(personId)
              : kind && personId
                ? await openForThread(kind, personId, topicThread)
                : { id: directId };
        if (cancelled) return;

        setPhase('joining');
        // A guest was joined by POST /join/:token/session itself, which
        // answered with everything /join would have (GuestJoinPage shapes
        // it); asking again would be a second knock at the door.
        const joined = guest?.joined || await join(opened.id);
        if (cancelled) return;

        sessionIdRef.current = joined.session.id;
        setSession(joined.session);
        setMe(joined.me);
        meRef.current = joined.me;
        setParticipants(joined.participants || []);
        // A guest with a waiting room ahead of them is not in the call yet:
        // 'lobby' until the feed says they were let in (or the join itself
        // says they already were - a guest who came back after a reload).
        const waitingAtDoor = guest && guest.lobby && joined.lobby !== 'admitted'
          && joined.me?.lobby_state !== 'admitted';
        if (joined.lobby === 'refused' || joined.me?.lobby_state === 'refused') {
          endedRef.current = true;
          setPhase('refused');
          guest?.onDone?.('refused');
          return;
        }
        if (!waitingAtDoor) setLobby(null);
        setPhase(joined.session.state !== 'open' ? 'ended' : waitingAtDoor ? 'lobby' : 'live');

        // Fetched after joining rather than before: a failure here costs the
        // relay, not the session, and the chat is worth having either way.
        iceConfig().then((c) => { if (!cancelled) { iceRef.current = c; setRelay(c); } })
          .catch(() => { if (!cancelled) setRelay({ relay: false, iceServers: [] }); });

        stopPollRef.current = pollLoop(opened.id,
          { since: joined.cursor, chatSince: joined.chat_cursor },
          (batch) => {
            if (cancelled) return;
            // The waiting room's answer, for a guest: 'waiting' keeps the
            // card up, 'refused' ends it here, 'admitted' opens the call.
            // Alone in the batch while waiting (no state, no participants),
            // so nothing below runs on it.
            if (batch.lobby) {
              if (batch.lobby === 'refused') {
                endedRef.current = true;
                if (stopPollRef.current) stopPollRef.current();
                setLobby('refused');
                setPhase('refused');
                guest?.onDone?.('refused');
                return;
              }
              if (batch.lobby === 'admitted') {
                setLobby(null);
                setPhase((p) => (p === 'lobby' ? 'live' : p));
              } else {
                // Still at the door: the batch carries nothing else worth
                // reading (no signals, nobody listed), by design.
                setLobby('waiting');
                setPhase((p) => (p === 'live' || p === 'lobby' ? 'lobby' : p));
                return;
              }
              if (batch.state === undefined) return;
            }
            setParticipants(batch.participants || []);
            if (batch.chat?.length) {
              setChat((prev) => {
                const seen = new Set(prev.map((m) => m.seq));
                return [...prev, ...batch.chat.filter((m) => !seen.has(m.seq))];
              });

              // A noise for a message that came FROM SOMEBODY ELSE.
              //
              // The feed does not filter chat by author the way it filters
              // signals - the transcript is the ticket's, so everybody gets
              // all of it - which means my own messages come back to me here.
              // Pinging on those would make the page beep at whoever is
              // typing, every time they typed, which is the fastest way to
              // get a notification turned off for good.
              const theirs = batch.chat.filter(
                (m) => m.participant_id !== joined.me?.id && !pinged.current.has(m.seq));
              for (const m of theirs) pinged.current.add(m.seq);
              if (theirs.length > 0) {
                if (!ping()) setMuzzled(true);
                // The half that works with the tab in the background, which
                // is where it will usually be: they are looking at the broken
                // machine, not at Jarvis.
                if (document.hidden) {
                  flashTitle(`${theirs.length} new message${theirs.length === 1 ? '' : 's'}`);
                }
              }
            }
            if (batch.state !== 'open') {
              endedRef.current = true;
              /*
               * WHY it ended, when the server says. The feed has carried
               * end_reason all along and this dropped it, so a caller whose
               * call was declined got the same blank "This session has ended"
               * as one whose colleague simply hung up - and since declining
               * was local until now, they usually got nothing at all and sat
               * on "Waiting for them to join" instead.
               *
               * Gordon: "if any user declines a call - the call sender popup
               * should close." It closes because the session leaves 'open';
               * this is what makes the closing say something.
               */
              if (batch.end_reason) setError(batch.end_reason);
              setPhase('ended');
              // An unanswered ring on a session that has ended is a page
              // ringing about nothing.
              silence();
              setAskedToShare(false);
              setAsked(false);
              closeAll();
              return;
            }
            // Through the ref, never the closure - see handleRef above - and
            // one at a time - see queueRef.
            for (const s of batch.signals || []) {
              queueRef.current = queueRef.current
                .then(() => handleRef.current?.(s))
                .catch(() => {});
            }
          },
          (why) => { if (!cancelled) setTrouble(why); });
      } catch (err) {
        if (cancelled) return;
        const body = err.response?.data;
        // A guest the organiser has already refused: the join itself says so.
        if (guest && body?.lobby === 'refused') {
          endedRef.current = true;
          setPhase('refused');
          guest.onDone?.('refused');
          return;
        }
        setError(body?.error || 'Could not open that session');
        setPhase(body?.cancelled || body?.ended ? 'ended' : 'error');
      }
    })();

    return () => {
      cancelled = true;
      /*
       * WALKING AWAY IS LEAVING, NEVER ENDING. A link in the menu, the
       * browser's Back, another call answered: the page goes, and with
       * people still in the call that is /leave - they carry on, and the
       * server ends it only if nobody else is there. Never /end, which would
       * hang up on everybody because one person clicked elsewhere. With
       * nobody else here yet it is left alone, as it always was: that is a
       * call still ringing, and the heartbeat settles it.
       */
      if (sessionIdRef.current && !endedRef.current && peersRef.current.length > 0) {
        endedRef.current = true;
        leaveSession(sessionIdRef.current).catch(() => {});
      }
      if (stopPollRef.current) stopPollRef.current();
      try { screenRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
      try { micRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
      closeAll();
    };
    // Deliberately keyed only on what identifies the session. Re-running this
    // on every state change would rejoin and re-poll on each keystroke in the
    // chat box.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId, directId, ticketId, kind, personId, guest?.sessionId]);

  /**
   * The tab closing, or reloading, mid-call: the same rule, by a request the
   * browser keeps alive after the page has gone (lib/sessionLink's
   * leaveOnUnload). Only where the credential is at hand synchronously - a
   * contractor's or a guest's; staff's comes from MSAL asynchronously, which
   * a closing page cannot wait for, so for them the heartbeat going stale
   * does it, as before.
   */
  useEffect(() => {
    const bye = () => {
      if (!sessionIdRef.current || endedRef.current || peersRef.current.length === 0) return;
      leaveOnUnload(sessionIdRef.current);
    };
    window.addEventListener('pagehide', bye);
    return () => window.removeEventListener('pagehide', bye);
  }, []);

  /**
   * A guest arriving while a recording is already running has no Recorder
   * to read GET /sessions/:id/recordings for them, so the banner is primed
   * from here with the same answer, shaped as the 'started' signal the
   * banner's store already understands. A refusal costs nothing.
   */
  useEffect(() => {
    if (!guest || phase !== 'live' || !sessionIdRef.current) return undefined;
    let live = true;
    sessionRecordings(sessionIdRef.current).then((r) => {
      const a = r?.active;
      if (!live || !a || a.state !== 'recording') return;
      onRecordingSignal({ kind: 'recording', from_id: a.by || null, payload: {
        state: 'started', recording_id: a.id, by_name: a.started_by_name || null, kind: a.kind, started_at: a.started_at
      } }, a.started_by_name || null);
    }).catch(() => {});
    return () => { live = false; };
  }, [guest, phase]);

  /** Let one guest in, or not; "Admit all" is this for each of them. */
  const decideGuest = useCallback(async (g, allow) => {
    if (!sessionIdRef.current) return;
    setAdmitting((cur) => new Set([...cur, g.id]));
    try {
      const r = await admitGuest(sessionIdRef.current, g.id, allow);
      setWaitingGuests((cur) => cur.filter((x) => x.id !== g.id));
      // The answer carries the list as it now stands, so the panel does
      // not wait a poll to agree with the button that was just pressed.
      if (Array.isArray(r?.participants)) setParticipants(r.participants);
      else setParticipants((cur) => cur.map((p) => (p.id === g.id ? { ...p, lobby_state: allow ? 'admitted' : 'refused' } : p)));
    } catch (err) {
      setTrouble(err.response?.data?.error || `Could not ${allow ? 'admit' : 'refuse'} ${g.name}.`);
    } finally {
      setAdmitting((cur) => { const next = new Set(cur); next.delete(g.id); return next; });
    }
  }, []);

  /** One signal from somebody else, routed to their connection by from_id. */
  const handle = useCallback(async (s) => {
    const mine = meRef.current;
    if (!mine || endedRef.current) return;
    const from = s.from_id;
    const nameOf = () => participantsRef.current.find((p) => p.id === from)?.display_name
      || null;
    try {
      /*
       * SOMEBODY IS AT THE DOOR - or was let in, or was not. Staff keep the
       * list; a guest's own page learns its fate from the feed's `lobby`
       * field instead (see the poll handler), so for them this is noise.
       */
      if (s.kind === 'lobby') {
        if (guest) return;
        const p = s.payload || {};
        const pid = p.participant_id || from;
        if (p.state === 'waiting') {
          setWaitingGuests((cur) => (cur.some((g) => g.id === pid) ? cur
            : [...cur, { id: pid, name: p.name || nameOf() || 'A guest', at: Date.now() }]));
          setLobbyOpen(true);
          if (!ping()) setMuzzled(true);
          if (document.hidden) flashTitle(`${p.name || 'A guest'} is waiting to join`);
        } else {
          setWaitingGuests((cur) => cur.filter((g) => g.id !== pid));
        }
        return;
      }
      if (s.kind === 'hello') {
        // Somebody arrived. Whether to offer is decided by the rule rather
        // than by who noticed first; the other side runs the same rule and
        // reaches the opposite answer. But a hello from somebody I ALREADY
        // have a connection with means their page started again - a reload -
        // and the connection I am holding is to a page that no longer
        // exists. Closed, so the pair is negotiated afresh.
        if (meshRef.current.has(from)) {
          closeEntry(from);
          setMeshTick((n) => n + 1);
        }
        return;
      }
      if (s.kind === 'offer') {
        await answer(from, s.payload);
        return;
      }
      if (s.kind === 'answer') {
        const entry = meshRef.current.get(from);
        // An answer to an offer that has since been replaced is for nothing.
        if (!entry || entry.pc.signalingState !== 'have-local-offer') return;
        await entry.pc.setRemoteDescription(s.payload);
        entry.haveRemote = true;
        for (const c of entry.pendingIce.splice(0)) {
          await entry.pc.addIceCandidate(c).catch(() => {});
        }
        return;
      }
      if (s.kind === 'candidate') {
        const entry = meshRef.current.get(from);
        if (!entry) {
          // Ahead of the offer it belongs to: the sender's browser produces
          // candidates the moment it has a description, which can be before
          // its offer has finished posting.
          const list = earlyIce.current.get(from) || [];
          list.push(s.payload);
          earlyIce.current.set(from, list);
          return;
        }
        if (!entry.haveRemote) { entry.pendingIce.push(s.payload); return; }
        await entry.pc.addIceCandidate(s.payload).catch(() => {});
        return;
      }
      if (s.kind === 'sharing') {
        const on = Boolean(s.payload?.sharing);
        // Named rather than assumed: a camera and a screen look identical in
        // a video element, and "their screen" over somebody's face is worse
        // than no label. The server's own report is about screens only, and
        // carries no source, so none means a screen.
        const source = s.payload?.source ? String(s.payload.source) : (on ? 'screen' : null);
        patchRemote(from, source ? { sharing: on, source } : { sharing: on });
        if (on) stoppedAt.current.delete(from); else stoppedAt.current.set(from, Date.now());
        // They have started: the question has been answered, so stop asking it.
        if (on) { setAsked(false); setDeclinedBy(null); }
        // Their screen has gone, so the marks on it go too - on every page,
        // each doing it for itself, from the sender's own report.
        if (!on && source !== 'camera') {
          drawEngine.forget(`share:${from}`);
          setAnnotating((a) => (a === `share:${from}` ? null : a));
        }
        return;
      }

      /*
       * THEY LEFT (POST /leave). Marked gone at once rather than on the next
       * presence list, so the tile, the header and - the one that matters
       * here - the Leave / End call buttons agree with the room straight away.
       */
      if (s.kind === 'bye') {
        setParticipants((cur) => cur.map((p) => (p.id === from ? { ...p, present: false } : p)));
        return;
      }

      /**
       * They are asking to see the screen.
       *
       * THE RING, AND WHY IT IS NOT ONLY A RING. The person being asked is
       * looking at their broken machine, not at Jarvis - so this makes a
       * noise, buzzes a phone if there is one, flashes the tab title, and puts
       * a prompt across the page. Any one of those can fail on its own: sound
       * is refused until a gesture, a phone may not vibrate, a title flash is
       * invisible on a focused tab. Together, at least one of them lands.
       *
       * Nothing here shares anything. It raises a question; getDisplayMedia
       * answers it, and the operating system asks again before anything is
       * visible.
       */
      if (s.kind === 'ask-share') {
        askedFrom.current = from;
        setAskedBy(nameOf());
        setAskedToShare(true);
        setDeclinedBy(null);
        const audible = startRinging();
        if (!audible) setMuzzled(true);
        buzz();
        if (document.hidden) flashTitle('IT is asking to see your screen');
        return;
      }

      if (s.kind === 'decline-share') {
        // So the fixer learns the answer rather than waiting for a screen that
        // is not coming. A refusal is a normal outcome of asking.
        setAsked(false);
        setDeclinedBy(nameOf() || 'They');
        return;
      }

      // Somebody is recording, asking to, or has stopped: the recorder's
      // own code reads it (components/recording/Recorder.js).
      if (s.kind === 'recording') { onRecordingSignal(s, nameOf()); return; }
    } catch (err) {
      console.warn(`Could not handle a ${s.kind}:`, err.message);
    }
  }, [answer, closeEntry, patchRemote, guest, drawEngine]);

  // Assigned during render rather than in an effect: the loop that reads it
  // starts in the mount effect, and an effect-assigned ref would be a tick
  // behind on the very first batch.
  handleRef.current = handle;

  /**
   * Keep the mesh in step with who is actually here.
   *
   * Driven by the presence list rather than by the hello signal, so it works
   * whichever of a pair arrived first: the later one sees the earlier in their
   * join response, and the earlier sees the later on the next poll. For each
   * person present with no connection, the pair's offerer offers (the other
   * waits for it); for each connection to somebody no longer present, the
   * connection is closed rather than held open to nobody.
   */
  useEffect(() => {
    if (phase !== 'live' || !me) return;
    const here = new Set(peers.map((p) => p.id));
    for (const id of [...meshRef.current.keys()]) {
      if (!here.has(id)) closeEntry(id);
    }
    for (const p of peers) {
      if (!meshRef.current.has(p.id) && shouldOffer(me, p)) offer(p);
    }
  }, [phase, me, peers, meshTick, offer, closeEntry]);

  /** Somebody I added has arrived: they are not ringing any more. */
  useEffect(() => {
    if (ringingOut.length === 0) return;
    const still = ringingOut.filter((r) => !peers.some((p) => samePerson(p, r)));
    if (still.length !== ringingOut.length) setRingingOut(still);
  }, [peers, ringingOut]);

  /** The overall state, for the header: connected to anybody is connected. */
  const connection = useMemo(() => {
    const states = peers.map((p) => remote[p.id]?.connection || 'new');
    if (states.includes('connected')) return 'connected';
    if (states.includes('connecting')) return 'connecting';
    if (states.length && states.every((x) => x === 'failed')) return 'failed';
    return 'new';
  }, [peers, remote]);

  /**
   * A video call opens with the camera already on.
   *
   * Waits for a connection rather than firing on mount: there is nobody to
   * send it to before then. Once only, so turning the camera off during a
   * video call does not have it switch itself back on. Whoever connects
   * later gets it the way any late joiner does.
   */
  const askedForCamera = useRef(false);
  useEffect(() => {
    if (!wantsVideo || askedForCamera.current) return;
    if (phase !== 'live' || connection !== 'connected') return;
    if (![...meshRef.current.values()].some((e) => e.videoSender)) return;
    askedForCamera.current = true;
    startCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsVideo, phase, connection]);

  useEffect(() => {
    if (!startedAt) return undefined;
    const id = setInterval(() => setElapsed((Date.now() - startedAt) / 1000),
      PRESENCE_TICK_MS);
    return () => clearInterval(id);
  }, [startedAt]);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ block: 'nearest' });
  }, [chat]);

  const say = async (event) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || !session?.id) return;
    setDraft('');
    try {
      const saved = await sendChat(session.id, body);
      // Shown at once rather than waiting for the poll to bring it back: the
      // feed does not echo your own messages, so without this the sender sees
      // nothing happen.
      setChat((prev) => (prev.some((m) => m.seq === saved.seq) ? prev : [...prev, saved]));
    } catch (err) {
      setTrouble(err.response?.data?.error || 'That message did not send');
      setDraft(body);
    }
  };

  /* ------------------------------------------------------- adding people */

  const openPicker = async () => {
    setPicking(true);
    setPickTrouble(null);
    setPeople(null);
    try {
      setPeople(await chatPeople());
    } catch {
      setPeople([]);
      setPickTrouble('Could not load the people you can add.');
    }
  };

  /**
   * Ring somebody into this call.
   *
   * The server decides whether this person may add that one, and answers 404
   * when not - the same answer as for somebody who does not exist, so the
   * picker cannot be used to find out who is there. Said plainly here.
   */
  const addPerson = async (row) => {
    setPickTrouble(null);
    try {
      const r = await inviteToSession(session.id, row.kind, row.id);
      setRingingOut((prev) => (prev.some((x) => x.key === (r?.key || row.key)) ? prev
        : [...prev, { key: r?.key || row.key, name: r?.name || row.name }]));
      setPicking(false);
    } catch (err) {
      const status = err.response?.status;
      setPickTrouble(status === 404 ? 'You cannot add them.'
        : status === 409 ? 'This call has ended.'
          : err.response?.data?.error || 'Could not add them.');
    }
  };

  const pickable = (people || []).filter((row) => !participants.some(
    (p) => (p.present || p.id === me?.id) && samePerson(p, row))
    && !ringingOut.some((x) => x.key === row.key));

  /* ------------------------------------------------------- the way out */

  // Whether THIS person ended it, which decides whether there is a reason
  // worth reading before going back.
  const endedByMe = useRef(false);

  const finish = async () => {
    if (!session?.id) return;
    stopSharing();
    endedByMe.current = true;
    try { await endSession(session.id, null); } catch { /* idempotent anyway */ }
    endedRef.current = true;
    setPhase('ended');
    setRingingOut([]);
    closeAll();
  };

  /**
   * Leave, and let the others carry on.
   *
   * With three or more people, hanging up is no longer ending the call for
   * everybody. The poll is stopped here, because unlike an end nothing in the
   * feed will ever say this session is over - it is not, for them.
   */
  const leave = async () => {
    if (!session?.id) return;
    stopSharing();
    endedByMe.current = true;
    endedRef.current = true;
    if (stopPollRef.current) stopPollRef.current();
    let ended = false;
    try { ended = Boolean((await leaveSession(session.id))?.ended); } catch { /* gone anyway */ }
    setLeft(!ended);
    setPhase('ended');
    setRingingOut([]);
    closeAll();
  };

  const about = aboutOf(session, me, topicThread, guest);
  const returnTo = cameFrom || about.back?.to || null;

  /**
   * Over: back to where it started.
   *
   * At once when this person hung up, left or cancelled - they know why.
   * After a pause when somebody else ended it or declined, so "Rita could not
   * take the call" is on the screen long enough to be read. The footer's own
   * button is there throughout for anybody who does not want to wait, and
   * `replace` keeps the dead session page out of the history, so pressing
   * the browser's Back from the chat does not land in an ended call.
   */
  useEffect(() => {
    // A guest goes back to GuestJoinPage's "Thanks for joining", not to a
    // route: they have none in Jarvis.
    if (phase === 'ended' && guest) { guest.onDone?.(left ? 'left' : 'ended', error); return undefined; }
    if (phase !== 'ended' || !returnTo) return undefined;
    const t = setTimeout(() => navigate(returnTo, { replace: true }),
      endedByMe.current ? 0 : RETURN_AFTER_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, returnTo]);

  /* ---------------------------------------------------------------- render */

  if (phase === 'opening' || phase === 'joining') {
    return (
      <div className="se-page">
        <p className="se-status">
          {phase === 'opening' ? 'Opening the session…' : 'Joining…'}
        </p>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="se-page">
        <div className="se-notice se-bad" role="alert">{error}</div>
        {guest ? (
          <button type="button" className="se-flat" onClick={() => guest.onDone?.('left')}>Leave</button>
        ) : (
          <button type="button" className="se-flat"
            onClick={() => navigate(cameFrom || -1)}>
            {backLabel(cameFrom)}
          </button>
        )}
      </div>
    );
  }

  if (phase === 'refused') {
    return (
      <div className="se-page">
        <div className="se-notice se-warn" role="status">The organiser did not admit you.</div>
      </div>
    );
  }

  /*
   * THE WAITING ROOM, from the guest's chair. A calm card and a pulse that
   * says the page is alive and asking; nothing of the call is drawn behind
   * it, because they are not in it. Leave is the one control.
   */
  if (phase === 'lobby') {
    return (
      <div className="se-page se-lobby-page" data-lobby={lobby || 'waiting'}>
        <header className="se-head">
          <div className="se-who">
            <h2 className="se-title">{about.title}</h2>
            <p className="se-sub">{guest?.meeting?.organiser_name ? `Organised by ${guest.meeting.organiser_name}` : 'Meeting'}</p>
          </div>
        </header>
        <div className="se-calling-scrim se-lobby-scrim" role="presentation">
          <div className="se-calling se-lobby" role="status" aria-live="polite">
            <span className="se-calling-pulse se-lobby-pulse" aria-hidden="true" />
            <strong className="se-calling-head">Waiting for someone to let you in…</strong>
            <span className="se-calling-sub">You are in the waiting room{guest?.name ? ` as ${guest.name}` : ''}.</span>
            <span className="se-calling-note">
              Somebody from NCMB in the meeting will see you are here and let you in. Keep this page open.
            </span>
            <div className="se-calling-buttons">
              <button type="button" className="se-flat se-lobby-leave" onClick={leave}>Leave</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // A guest is a 'user' to the call - they show, they do not take.
  const iAmUser = me?.role === 'user' || Boolean(guest);
  const waiting = peers.length === 0;
  // A meeting's call rings nobody: the invited come from the meeting page
  // when the time comes and guests wait at the door. The first person in
  // used to get the "Calling them… Cancel the call" scrim over the page,
  // which also sat over the waiting-room panel, so the organiser alone in
  // the room could not admit a guest until somebody else joined.
  const inMeeting = Boolean(session?.meeting_id || session?.meeting?.id);
  // One other person is the call as it always was: one big stage. Two or more
  // is a grid of tiles.
  const group = peers.length >= 2;
  // Tiles rather than one big stage: a group, or anything else on the stage
  // beside the one other person - my own screen's preview, the whiteboard.
  const tiled = group || sharing || board.shown;
  const isGroupCall = Boolean(session?.chat_group_id);
  const remoteOf = (id) => remote[id] || FRESH;
  const anySharing = peers.some((p) => remoteOf(p.id).sharing);
  // Somebody I would ask is already showing something: the question has been
  // answered, so the button for asking it goes until they stop.
  const targetSharing = askTargets.some((p) => remoteOf(p.id).sharing);

  // A picture arriving on somebody's tile - unless they have just said they
  // stopped (stoppedAt, above).
  const pictureArrived = (id, el) => {
    if (!(el.videoWidth > 0)) return;
    if (Date.now() - (stoppedAt.current.get(id) || 0) < 4000) return;
    patchRemote(id, { sharing: true });
  };

  // Element registration for a tile, so a track arriving can find it and a
  // tile drawn after the track arrived still gets it.
  // And the chosen speakers, on every element as it is drawn - the effect
  // above only reaches the ones already on the page.
  const videoRef = (id) => (el) => {
    if (!el) { videoEls.current.delete(id); return; }
    videoEls.current.set(id, el);
    const stream = meshRef.current.get(id)?.stream;
    if (stream && el.srcObject !== stream) el.srcObject = stream;
    if (el.dataset.sink !== (devices.audiooutput?.id || '')) {
      el.dataset.sink = devices.audiooutput?.id || '';
      applySink(el, devices.audiooutput?.id);
    }
  };
  const audioRef = (id) => (el) => {
    if (!el) { audioEls.current.delete(id); return; }
    audioEls.current.set(id, el);
    const stream = meshRef.current.get(id)?.audioStream;
    if (stream && el.srcObject !== stream) el.srcObject = stream;
    if (el.dataset.sink !== (devices.audiooutput?.id || '')) {
      el.dataset.sink = devices.audiooutput?.id || '';
      applySink(el, devices.audiooutput?.id);
    }
  };

  /** The words on an empty stage, which are the instructions. */
  const guidance = (
    <div className="se-stage-empty">
      {iAmUser ? (
        <>
          <p className="se-stage-lead">
            {sharing
              ? 'You are sharing your screen.'
              : 'Nothing is being shared yet.'}
          </p>
          <p className="se-stage-note">
            {sharing
              ? 'They can see what you share. Stop any time — from the '
                + 'button below, or from your own browser\'s sharing bar.'
              : 'When they ask, press Share my screen. Your browser will '
                + 'ask you which window to share, and you can stop at any '
                + 'moment.'}
          </p>
          {/* The one thing worth saying unprompted in a firm that holds
              client files: share a window, not the whole screen. */}
          {!sharing && (
            <p className="se-stage-tip">
              Choose a single <strong>window</strong> rather than your whole
              screen, so nothing else you have open is visible.
            </p>
          )}
        </>
      ) : (
        <>
          <p className="se-stage-lead">Their screen is not shared yet.</p>
          <p className="se-stage-note">
            Ask them to press <strong>Share my screen</strong>. Only they can
            start it, and only they can stop it — you are watching, not
            controlling.
          </p>
          <p className="se-stage-note">
            You can show your own screen too, with Share my screen below.
          </p>
        </>
      )}
    </div>
  );

  /** One other person's tile: their video when they share, their name always. */
  const tile = (p) => {
    const r = remoteOf(p.id);
    const name = p.display_name || 'Somebody';
    // A SCREEN, not a camera: only a share can be marked up, and its marks
    // are kept under the sharer's id ('share:<id>') on every page.
    const screenShare = r.sharing && r.source !== 'camera';
    const target = `share:${p.id}`;
    return (
      <div key={p.id} className="se-tile" data-id={p.id} data-share={(tiled && screenShare) || undefined}
        data-focus={bigId === p.id || undefined} data-solo={!tiled || undefined}>
        {tiled && (
          <div className="se-tile-head">
            <span className="se-tile-name">
              {name}{p.role === 'fixer' && <span className="se-tile-tag"> (IT)</span>}
              {p.is_guest && <span className="se-tile-tag"> (guest)</span>}
            </span>
            <span className="se-tile-state" title={stateWords(r.connection)}>
              <span className={`se-dot se-dot-${r.connection}`} aria-hidden="true" />
              <span className="se-tile-conn">
                {stateWords(r.connection)}
                {r.connection === 'connected' && !r.audio ? ' · no sound' : ''}
              </span>
            </span>
          </div>
        )}
        {/* Their video. Muted on the element itself: the audio comes through
            its own element, and leaving it here as well is how a session
            echoes. */}
        {/* THE POSITIVE THAT CANNOT LIE. Sharing is normally learned from
            the sender's own report, which is authoritative because their
            page is the only thing that knows - but if that one POST fails,
            the receiver would show nothing while frames poured in. A video
            element only reaches loadedmetadata when it has real dimensions,
            and an empty transceiver never produces a frame, so this is a
            true positive where the track's own `muted` and `onunmute` are
            not. */}
        <video
          ref={videoRef(p.id)}
          className="se-video"
          data-live={r.sharing || undefined}
          autoPlay
          playsInline
          muted
          onLoadedMetadata={(e) => pictureArrived(p.id, e.currentTarget)}
          onResize={(e) => pictureArrived(p.id, e.currentTarget)}
        />
        <audio ref={audioRef(p.id)} autoPlay />

        {!r.sharing && (tiled
          ? (
            <div className="se-tile-empty" aria-hidden="true">
              <Avatar person={{ key: p.key, name }} className="se-tile-face" size={80} text={initialsOf(name)} />
            </div>
          )
          : guidance)}

        {/* MAXIMISE, on the video itself, where somebody looking at a screen
            they cannot read will reach for it. Only when there is something
            to maximise. */}
        {r.sharing && (
          <div className="se-stage-bar">
            <span className="se-stage-label">
              {r.source === 'camera' ? `${name} on camera` : `${name}'s screen`}
            </span>
            <span className="se-stage-actions">
              {/* Mark it up - for everybody, without touching what is shown. */}
              {screenShare && (
                <button type="button" className="se-annot dr-annot-toggle"
                  aria-pressed={annotating === target}
                  onClick={() => setAnnotating((a) => (a === target ? null : target))}
                  title="Draw, highlight or point on this screen — everybody sees the marks; the screen itself is not changed">
                  Annotate
                </button>
              )}
              <button type="button" className="se-max" onClick={() => maximise(p.id)}
                aria-pressed={bigId === p.id}>
                {bigId === p.id ? 'Exit full screen' : 'Full screen'}
              </button>
            </span>
          </div>
        )}
        {/* The glass over their screen: everybody's marks, always shown;
            my pen only while Annotate is on. */}
        {screenShare && (
          <Annotate engine={drawEngine} target={target} getVideo={videoOf(p.id)}
            active={annotating === target} onClose={() => setAnnotating(null)}
            whose={`${name}'s screen`} />
        )}
      </div>
    );
  };

  /*
   * MY SCREEN, AS THEY SEE IT. A tile of its own while I share, with the
   * same glass over it as everybody else's copy - so the presenter sees the
   * marks the room is making, and can make them too.
   */
  const myTarget = `share:${me?.id}`;
  const previewTile = sharing && (
    <div key="mine" className="se-preview" data-id={me?.id} data-mine=""
      data-annotating={annotating === myTarget || undefined}>
      <div className="se-tile-head">
        <span className="se-tile-name">
          Your screen <span className="se-tile-tag">· what everybody sees</span>
        </span>
      </div>
      <video ref={previewRef} className="se-video" data-live autoPlay playsInline muted />
      <div className="se-preview-bar">
        <span className="se-preview-label">You are sharing this</span>
        <span className="se-stage-actions">
          <button type="button" className="se-annot dr-annot-toggle" aria-pressed={annotating === myTarget}
            onClick={() => setAnnotating((a) => (a === myTarget ? null : myTarget))}>
            Annotate
          </button>
          <button type="button" className="se-stop se-stop-mini" onClick={stopSharing}>Stop sharing</button>
        </span>
      </div>
      <Annotate engine={drawEngine} target={myTarget} getVideo={videoOf('mine')} presenter
        active={annotating === myTarget} onClose={() => setAnnotating(null)} whose="your screen" />
    </div>
  );

  const withWhom = namesOf(peers);

  return (
    <div className="se-page">
      <header className="se-head">
        <div className="se-who">
          <h2 className="se-title">{about.title}</h2>
          <p className="se-sub">
            {phase === 'ended'
              ? (left ? 'You left the call.' : 'This session has ended.')
              : waiting
                ? (iAmUser && !isGroupCall
                  ? 'Waiting for somebody from IT to join.'
                  : 'Waiting for them to join.')
                : `With ${withWhom}`}
          </p>
        </div>
        {phase === 'live' && !guest && atTheDoor.length > 0 && (
          <button type="button" className="se-lobby-chip" aria-expanded={lobbyOpen}
            onClick={() => setLobbyOpen((v) => !v)} title="Guests waiting to be let in">
            <span className="se-lobby-chip-dot" aria-hidden="true" />
            Waiting room ({atTheDoor.length})
          </button>
        )}
        {phase === 'live' && (
          <div className="se-state">
            <span className={`se-dot se-dot-${connection}`} aria-hidden="true" />
            <span className="se-conn">
              {connection === 'connected' ? `Connected · ${spoken(elapsed)}`
                : connection === 'connecting' ? 'Connecting…'
                  : connection === 'failed' ? 'Could not connect'
                    : waiting ? 'Nobody else here yet' : 'Setting up…'}
            </span>
          </div>
        )}
      </header>

      {/* "Recording · started by Gordon · 03:12", for everybody, the moment
          anybody records (components/recording). */}
      {phase === 'live' && <RecordingBanner />}

      {/* WHO IS AT THE DOOR, for staff. A panel under the chip: each guest
          by name with Admit / Refuse, and Admit all when there are several.
          Open by itself when somebody arrives, so the organiser does not
          have to notice a chip; the chip folds it away and brings it back. */}
      {phase === 'live' && !guest && atTheDoor.length > 0 && lobbyOpen && (
        <div className="se-lobby-panel" role="region" aria-label="Waiting room">
          <div className="se-lobby-top">
            <strong className="se-lobby-head">
              {atTheDoor.length === 1 ? `${atTheDoor[0].name} (guest) is waiting`
                : `${atTheDoor.length} guests are waiting`}
            </strong>
            {atTheDoor.length > 1 && (
              <button type="button" className="se-flat se-admit-all"
                onClick={() => atTheDoor.forEach((g) => decideGuest(g, true))}>
                Admit all
              </button>
            )}
          </div>
          <ul className="se-lobby-list">
            {atTheDoor.map((g) => (
              <li key={g.id} className="se-lobby-row" data-guest={g.id}>
                <span className="se-tile-face se-lobby-face" aria-hidden="true">{initialsOf(g.name)}</span>
                <span className="se-lobby-name">{g.name} <span className="se-tile-tag">(guest)</span></span>
                <button type="button" className="se-share se-admit" disabled={admitting.has(g.id)}
                  onClick={() => decideGuest(g, true)}>Admit</button>
                <button type="button" className="se-flat se-refuse" disabled={admitting.has(g.id)}
                  onClick={() => decideGuest(g, false)}>Refuse</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* THE CALLER'S HALF OF THE POPUP.
          //
          // Gordon asked for a popup at both ends - "when the call is sent or
          // being received". The receiving side gets IncomingCall's modal; this
          // is the sending side, and before it the caller had a line of text
          // on a page saying "Waiting for them to join", which is not the same
          // thing at all: it looks like a page that has finished loading
          // rather than a phone that is ringing somewhere else.
          //
          // Only while there is genuinely nobody else there. The moment
          // anybody answers it goes, because then the page itself is the call. */}
      {phase === 'live' && waiting && !inMeeting && (
        <div className="se-calling-scrim" role="presentation">
          <div className="se-calling" role="status" aria-live="polite">
            <span className="se-calling-pulse" aria-hidden="true" />
            <strong className="se-calling-head">
              {isGroupCall ? 'Calling the group' : iAmUser ? 'Calling IT' : 'Calling them'}
            </strong>
            <span className="se-calling-sub">
              {isGroupCall && session?.group_name
                ? `#${session.group_name}`
                : about.sub || 'Waiting for somebody to answer'}
            </span>
            <span className="se-calling-note">
              {isGroupCall
                ? 'It is ringing on their screens. Whoever answers first joins you.'
                : 'It is ringing on their screen. They may be a moment.'}
            </span>
            <div className="se-calling-buttons">
              {/* Cancelling ENDS the session, which is what stops the ring at
                  the other end. Walking away from the page would leave it
                  ringing at somebody until the presence heartbeat went stale. */}
              <button type="button" className="se-flat" onClick={finish}>
                Cancel the call
              </button>
            </div>
          </div>
        </div>
      )}

      {!secure && (
        <div className="se-notice se-bad" role="alert">
          Screen sharing and the microphone both need a secure connection.
          Open Jarvis over https.
        </div>
      )}

      {/* A missing relay breaks nothing most of the time, which is exactly why
          it has to be said: it fails for about one person in six, with no
          message of its own. */}
      {relay && relay.relay === false && connection === 'failed' && (
        <div className="se-notice se-bad" role="alert">
          The browsers could not reach each other directly, and no relay is
          configured for Jarvis. Chat still works. Tell whoever runs Jarvis that
          a TURN relay is needed.
        </div>
      )}
      {relay && relay.relay === false && connection !== 'failed' && (
        <p className="se-hint">
          No relay is configured, so a few connections will not succeed — usually
          on a mobile network or behind a strict firewall.
        </p>
      )}

      {/* SOMEBODY IS WAITING ON YOU, across the page, in words, whether or not
          the sound worked. Sound can be refused, a phone may not buzz and a
          title flash is invisible on a focused tab - so this is the one that
          never fails, and the others are there to make somebody look at it. */}
      {askedToShare && phase === 'live' && (
        <div className="se-ask" role="alertdialog" aria-labelledby="se-ask-head">
          <div className="se-ask-text">
            <strong id="se-ask-head" className="se-ask-head">
              {askedBy ? `${askedBy} is asking to see your screen` : 'IT is asking to see your screen'}
            </strong>
            <span className="se-ask-note">
              You choose what to show, and you can stop at any time. Pick a single
              window rather than your whole screen.
              {group ? ' Everybody in the call will see it.' : ''}
            </span>
          </div>
          <div className="se-ask-buttons">
            <button type="button" className="se-share" onClick={acceptAsk}>
              Share my screen
            </button>
            <button type="button" className="se-flat" onClick={declineAsk}>
              Not just now
            </button>
          </div>
        </div>
      )}

      {/* The honest version of "we tried to make a noise and were not allowed".
          Showing nothing here is how somebody concludes the feature does not
          work. */}
      {muzzled && sound && phase === 'live' && (
        <div className="se-notice se-warn se-muzzled" role="status">
          <span>Your browser is holding back sounds until you interact with this page.</span>
          <button type="button" className="se-flat" onClick={enableSound}>
            Turn sound on
          </button>
        </div>
      )}

      {asked && !targetSharing && phase === 'live' && (
        <p className="se-hint" role="status">
          Asked{askedWhom ? ` ${askedWhom}` : ''} to share — their screen should
          ring. They can refuse, and you will be told if they do.
        </p>
      )}

      {declinedBy && phase === 'live' && (
        <div className="se-notice se-warn" role="status">
          {declinedBy} said not just now. Ask again when they are ready, or carry
          on in the chat.
        </div>
      )}

      {ringingOut.length > 0 && phase === 'live' && (
        <div className="se-ringing" role="status" aria-live="polite">
          {ringingOut.map((r) => (
            <span key={r.key} className="se-chip">
              <span className="se-chip-dot" aria-hidden="true" />
              Ringing {r.name}…
            </span>
          ))}
        </div>
      )}

      {trouble && <div className="se-notice se-warn" role="status">{trouble}</div>}
      {connection === 'failed' && phase === 'live' && (
        <div className="se-notice se-warn" role="alert">
          The call did not connect. The chat below still works.
        </div>
      )}

      <div className="se-body">
        <section className="se-stage" ref={stageRef} data-big={bigId || undefined}
          data-count={peers.length} data-board={(board.shown && phase === 'live') || undefined}>
          {/* "You are sharing your screen · Stop", where the eye already is -
              the top of the stage, as Teams has it - for every role. The
              footer has the same button; the browser's own bar is the third
              way, and all three land in stopSharing. */}
          {sharing && phase === 'live' && (
            <div className="se-sharepill" role="status">
              <span className="se-sharing-dot" aria-hidden="true" />
              <span className="se-sharepill-text">
                You are sharing your screen{group ? ' with everybody' : ''}
              </span>
              <button type="button" className="se-sharepill-stop" onClick={stopSharing}>
                Stop sharing
              </button>
            </div>
          )}

          {/* THE WHITEBOARD, as a stage tile above the people. */}
          {board.shown && phase === 'live' && (
            <Whiteboard engine={drawEngine} onHide={hideBoard} people={peers.length + 1} />
          )}

          {waiting && !sharing ? guidance : (
            <div className="se-grid" data-group={tiled || undefined}>
              {previewTile}
              {peers.map(tile)}
              {group && !anySharing && iAmUser && !sharing && (
                <p className="se-grid-note">
                  When IT asks, press Share my screen — everybody in the call will
                  see the window you choose.
                </p>
              )}
            </div>
          )}

          {/* ME, small. Always in a group, so I can see I am in it; with one
              other person only while my camera is on, which is the one thing
              of mine worth seeing. */}
          {phase === 'live' && (group || cameraOn) && (
            <div className="se-self" aria-label="You">
              <video ref={selfVideoRef} className="se-self-video" data-live={cameraOn || undefined}
                autoPlay playsInline muted />
              {!cameraOn && (
                <Avatar person={{ key: me?.key, name: me?.display_name }} className="se-tile-face se-self-face"
                  aria-hidden="true" text={initialsOf(me?.display_name)} />
              )}
              <span className="se-self-label">
                You{sharing ? ' · sharing your screen' : ''}{micOn ? '' : ' · muted'}
              </span>
            </div>
          )}

          {cameraOn && !sharing && (
            <p className="se-sharing" role="status">
              <span className="se-sharing-dot" aria-hidden="true" />
              Your camera is on
            </p>
          )}
        </section>

        <section className="se-side">
          <div className="se-chat" aria-label="Chat">
            {chat.length === 0 && (
              <p className="se-chat-empty">
                {isGroupCall || session?.direct_thread_id
                  ? 'Anything typed here stays with the call afterwards — useful '
                    + 'for a serial number or a command to copy.'
                  : 'Anything typed here stays on the ticket afterwards — useful for a '
                    + 'serial number or a command to copy.'}
              </p>
            )}
            {chat.map((m) => (
              <div key={m.seq} className="se-line"
                data-mine={m.participant_id === me?.id || undefined}>
                <span className="se-line-who">{m.author}</span>
                <span className="se-line-body">{m.body}</span>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          <form className="se-say" onSubmit={say}>
            <label className="se-say-label" htmlFor="se-draft">Message</label>
            <input
              id="se-draft"
              type="text"
              value={draft}
              placeholder={phase === 'ended' ? 'The session has ended' : 'Type a message'}
              disabled={phase !== 'live'}
              onChange={(e) => setDraft(e.target.value)}
            />
            <button type="submit" disabled={phase !== 'live' || !draft.trim()}>Send</button>
          </form>
        </section>
      </div>

      {picking && phase === 'live' && (
        <div className="se-pick" role="dialog" aria-labelledby="se-pick-head">
          <div className="se-pick-top">
            <strong id="se-pick-head">Add somebody to the call</strong>
            <button type="button" className="se-flat" onClick={() => setPicking(false)}>
              Close
            </button>
          </div>
          {pickTrouble && <div className="se-notice se-warn" role="status">{pickTrouble}</div>}
          {people === null ? (
            <p className="se-hint">Loading…</p>
          ) : pickable.length === 0 ? (
            <p className="se-hint">Everybody you can add is already here.</p>
          ) : (
            <ul className="se-pick-list">
              {pickable.map((row) => (
                <li key={row.key}>
                  <button type="button" className="se-pick-row" onClick={() => addPerson(row)}>
                    <Avatar person={row} className="se-tile-face" aria-hidden="true" text={initialsOf(row.name)} />
                    <span className="se-pick-name">
                      {row.name}
                      {row.is_contractor && <span className="se-tile-tag"> (IT)</span>}
                    </span>
                    <span className="se-pick-online" data-on={row.online || undefined}>
                      {row.online ? 'Online' : 'Away'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* THE DEVICES, MID-CALL. The same panel as the call menu's, so what
          was learned there applies here; the switch itself is chooseDevice. */}
      {devicesOpen && phase === 'live' && (
        <div className="se-devices" role="dialog" aria-labelledby="se-devices-head">
          <div className="se-pick-top">
            <strong id="se-devices-head">Devices</strong>
            <button type="button" className="se-flat" onClick={() => setDevicesOpen(false)}>
              Close
            </button>
          </div>
          <p className="se-hint">
            Changes take effect at once, and are remembered for your next call.
          </p>
          <DevicePanel choice={devices} onChoose={chooseDevice}
            meterStream={micRef.current} active={activeDevices} note={deviceNote}
            idPrefix="se-dev" />
        </div>
      )}

      {phase === 'live' && confirmEnd && group && !guest && (
        <div className="se-confirm" role="alertdialog" aria-labelledby="se-confirm-head"
          aria-describedby="se-confirm-note"
          onKeyDown={(e) => { if (e.key === 'Escape') setConfirmEnd(false); }}>
          <div className="se-confirm-text">
            <strong id="se-confirm-head" className="se-confirm-head">
              End the call for all {peers.length + 1} people?
            </strong>
            <span id="se-confirm-note" className="se-confirm-note">
              Everybody is disconnected, not just you. To go and let the others
              carry on, choose Leave instead.
            </span>
          </div>
          <div className="se-confirm-buttons">
            <button type="button" className="se-end-yes"
              onClick={() => { setConfirmEnd(false); finish(); }}>
              End for everyone
            </button>
            <button type="button" className="se-flat se-confirm-leave"
              onClick={() => { setConfirmEnd(false); leave(); }}>
              Leave instead
            </button>
            <button type="button" className="se-flat se-confirm-cancel" autoFocus
              onClick={() => setConfirmEnd(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {phase === 'live' && (
        <footer className="se-bar">
          {/* EVERYBODY MAY SHARE (22 October). The two-person design let only
              the user show their screen, because the call was the user's
              machine and IT watching it; with a meeting, a group and a
              whiteboard, the person presenting is whoever has something to
              show. One screen at a time each - getDisplayMedia is one track -
              and two people sharing is two tiles. Stopping is the same
              button, and the pill at the top of the stage. */}
          {sharing
            ? (
              <button type="button" className="se-stop" onClick={stopSharing}>
                Stop sharing
              </button>
            )
            : (
              <button type="button" className="se-share" onClick={startSharing}>
                Share my screen
              </button>
            )}

          {/* The fixer asks; they cannot take. Disabled with nobody there,
              because asking an empty room is how somebody concludes the button
              does nothing. */}
          {!iAmUser && !guest && !targetSharing && (
            <button type="button" className="se-ask-btn" onClick={askToShare}
              disabled={waiting}>
              {asks > 0 ? 'Ask again' : 'Ask to see their screen'}
            </button>
          )}

          <button type="button" className="se-flat" onClick={toggleMic}
            aria-pressed={micOn}>
            {micOn ? 'Mute' : micRefused ? 'Microphone blocked' : 'Unmute'}
          </button>

          {/* One video channel each, so this is disabled while a screen is up
              rather than silently replacing it. */}
          <button type="button" className="se-flat" onClick={toggleCamera}
            aria-pressed={cameraOn} disabled={sharing}
            title={sharing
              ? 'Stop sharing your screen first — there is one video channel each'
              : 'Show your camera'}>
            {cameraOn ? 'Camera off' : cameraRefused ? 'Camera blocked' : 'Camera on'}
          </button>

          {/* The whiteboard: the first to open it opens it for everybody; Hide
              (here or on the board) is for me only. A dot when somebody has
              drawn on it while it was hidden. */}
          <button type="button" className="se-flat se-board-btn" aria-pressed={board.shown}
            onClick={() => (board.shown ? hideBoard() : openBoard())}
            title={board.shown ? 'Hide the whiteboard (for you only)'
              : board.live ? 'Show the whiteboard' : 'Open a whiteboard for everybody in the call'}>
            Whiteboard
            {board.unseen && !board.shown && <span className="se-board-dot" aria-label=" (new drawing)" />}
          </button>

          <button type="button" className="se-flat" onClick={toggleSound}
            aria-pressed={sound}
            title="Whether this page makes a noise when you are needed">
            {sound ? 'Sound on' : 'Sound off'}
          </button>

          {/* Not for a guest: who is in a meeting is the organiser's call. */}
          {!guest && (
            <button type="button" className="se-flat se-add" onClick={openPicker}
              aria-expanded={picking}>
              Add people
            </button>
          )}

          {/* Record, at the user's option (Gordon, 22 October). The control,
              the choice, the consent prompt and the capture all live in
              components/recording; the join response says whether the firm
              allows it and whether people must be asked first. */}
          {/* A guest sees the banner when somebody records, and never the
              button: recording a meeting is the firm's to decide. */}
          {!guest && (
            <Recorder sessionId={session?.id} me={me} participants={participants} phase={phase}
              micRef={micRef} meshRef={meshRef} video={wantsVideo || cameraOn || sharing || anySharing || board.shown}
              config={session?.recording || null} />
          )}

          {/* Which microphone, speakers and camera - changeable without
              hanging up, which is when somebody finds out the wrong one is on. */}
          <button type="button" className="se-flat se-devices-btn"
            onClick={() => setDevicesOpen((v) => !v)} aria-expanded={devicesOpen}
            title="Choose your microphone, speakers and camera">
            Devices
          </button>

          <span className="se-spacer" />

          {/* LEAVE IS NOT END once there are three of you. With one other
              person, hanging up and ending are the same act, and the button
              says so. With two or more, the ordinary thing is to leave them
              to it - and ending it for everybody is still there, but as the
              second choice rather than the one under somebody's thumb. */}
          {guest ? (
            // A guest leaves; ending a meeting for everybody is not theirs to do.
            <button type="button" className="se-leave se-guest-leave" onClick={leave}>Leave</button>
          ) : group ? (
            <>
              {/* Anybody may end it for everybody - an organiser who has gone
                  is not a reason for a call to run on - but never by the
                  slip of a thumb: it asks first, naming how many it ends. */}
              <button type="button" className="se-flat se-end-all" onClick={() => setConfirmEnd(true)}
                aria-haspopup="dialog" aria-expanded={confirmEnd}>
                End for everyone
              </button>
              <button type="button" className="se-leave" onClick={leave}>Leave</button>
            </>
          ) : (
            <button type="button" className="se-end" onClick={finish}>End call</button>
          )}
        </footer>
      )}

      {phase === 'ended' && (
        <footer className="se-bar">
          <p className="se-ended">
            {left ? 'You left the call. The others can carry on without you.'
              : error || (isGroupCall
                ? 'The session has ended. A line about it is in the group.'
                : session?.direct_thread_id
                  ? 'The session has ended. A line about it is in your conversation.'
                  : 'The session has ended. What was typed stays on the ticket.')}
          </p>
          <span className="se-spacer" />
          <button type="button" className="se-flat"
            onClick={() => navigate(returnTo || -1, returnTo ? { replace: true } : undefined)}>
            {cameFrom ? backLabel(cameFrom) : (about.back ? about.back.label : 'Go back')}
          </button>
        </footer>
      )}

      {micRefused && phase === 'live' && (
        <p className="se-hint">
          Your microphone is not available, so talk in the chat instead. The screen
          sharing is unaffected.
        </p>
      )}
    </div>
  );
};

export default SessionPage;
